import React, { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaCalendarAlt, FaMoneyBillWave, FaChartBar, FaSpinner } from 'react-icons/fa';
import { newFinanceService, formatCurrencyTotals } from '../../services/newFinance.service';
import type { CurrencyTotals, FinanceRecord } from '../../types/newFinance';
import { OFFERING_TYPE_LABELS } from '../../types/newFinance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  personName: string;
}

interface PersonalStats {
  totalRecords: number;
  titheTotal: CurrencyTotals;
  thanksgivingTotal: CurrencyTotals;
  otherTotal: CurrencyTotals;
  grandTotal: CurrencyTotals;
  records: FinanceRecord[];
}

export const PersonalOfferingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  personName
}) => {
  const [stats, setStats] = useState<PersonalStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // 데이터 로드
  const loadPersonalStats = async () => {
    if (!personName) return;
    
    setLoading(true);
    try {
      const data = await newFinanceService.getPersonalOfferingStats(personName, selectedYear);
      setStats(data);
      
      // 사용 가능한 연도 목록 생성 (전체 기록에서)
      const allRecords = await newFinanceService.searchFinanceRecordsByName(personName);
      const years = Array.from(new Set(
        allRecords.map(record => new Date(record.date).getFullYear())
      )).sort((a, b) => b - a);
      setAvailableYears(years);
    } catch (error) {
      console.error('개인 통계 로딩 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && personName) {
      loadPersonalStats();
    }
  }, [isOpen, personName, selectedYear]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="sticky top-0 bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaUser className="h-6 w-6 text-white mr-3" />
              <div>
                <h2 className="text-xl font-bold text-white">{personName}님의 헌금 내역</h2>
                <p className="text-green-100">{selectedYear}년 통계</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* 연도 선택 */}
              {availableYears.length > 0 && (
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg border border-white border-opacity-30 focus:ring-2 focus:ring-white focus:ring-opacity-50"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year} className="text-gray-800">
                      {year}년
                    </option>
                  ))}
                </select>
              )}
              
              <button
                onClick={onClose}
                className="text-white hover:text-green-200 transition-colors"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* 본문 */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <FaSpinner className="h-8 w-8 animate-spin text-green-500" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">데이터 로딩 중...</span>
            </div>
          ) : !stats ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FaUser className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>해당 이름의 헌금 내역을 찾을 수 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* 통계 카드들 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* 총 기록 수 */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <FaCalendarAlt className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">총 기록</h3>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.totalRecords}건</p>
                    </div>
                  </div>
                </div>

                {/* 십일조 합계 */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <FaMoneyBillWave className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">십일조</h3>
                      <p className="text-lg font-bold text-green-900 dark:text-green-100">
                        {formatCurrencyTotals(stats.titheTotal)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 감사헌금 합계 */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <FaChartBar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">감사헌금</h3>
                      <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                        {formatCurrencyTotals(stats.thanksgivingTotal)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 기타헌금 합계 */}
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <FaMoneyBillWave className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200">기타헌금</h3>
                      <p className="text-lg font-bold text-orange-900 dark:text-orange-100">
                        {formatCurrencyTotals(stats.otherTotal)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 총 합계 */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-6 border-2 border-green-200 dark:border-green-700">
                <div className="text-center">
                  <h3 className="text-xl font-bold text-green-800 dark:text-green-200 mb-2">
                    {selectedYear}년 총 헌금 합계
                  </h3>
                  <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                    {formatCurrencyTotals(stats.grandTotal)}
                  </p>
                </div>
              </div>

              {/* 상세 내역 테이블 */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  📋 상세 헌금 내역
                </h3>
                
                {stats.records.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    {selectedYear}년에 기록된 헌금 내역이 없습니다.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                      <thead className="bg-gray-100 dark:bg-gray-600">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            날짜
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            헌금 종류
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            금액
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            내용
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                        {stats.records.map(record => {
                          const personalItems = [
                            // 십일조
                            ...record.titheItems
                              .filter(item => item.name === personName)
                              .map(item => ({
                                ...item,
                                type: 'tithe' as const,
                                typeName: '십일조',
                                date: record.date
                              })),
                            // 감사헌금
                            ...record.thanksgivingItems
                              .filter(item => item.name === personName)
                              .map(item => ({
                                ...item,
                                type: 'thanksgiving' as const,
                                typeName: '감사헌금',
                                date: record.date
                              })),
                            // 기타헌금
                            ...record.otherIncomeItems
                              .filter(item => item.name === personName)
                              .map(item => ({
                                ...item,
                                type: 'other' as const,
                                typeName: OFFERING_TYPE_LABELS[item.category] || item.category,
                                date: record.date
                              }))
                          ];

                          return personalItems.map((item, itemIndex) => (
                            <tr key={`${record.id}-${item.type}-${itemIndex}`} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                {item.date}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  item.type === 'tithe' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                  item.type === 'thanksgiving' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' :
                                  'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                                }`}>
                                  {item.typeName}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-right text-gray-900 dark:text-white">
                                {item.amount.toLocaleString()} {item.currency}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                {item.description || '-'}
                              </td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 rounded-b-2xl">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};